import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  CreditCard as CardIcon,
  AccountBalance as BankIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { stripePromise } from '../../config/stripe';
import { paymentMethodsAPI } from '../../services/stripeAPI';

interface PaymentMethod {
  paymentMethodId: string;
  type: 'card' | 'bank_account';
  card?: {
    brand: string;
    last4: string;
    expMonth: number;
    expYear: number;
  };
  bankAccount?: {
    bankName: string;
    last4: string;
    accountType: string;
  };
  created: string;
}

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
  },
};

function AddCardDialog({ open, onClose, onSuccess }: { open: boolean; onClose: () => void; onSuccess: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!stripe || !elements) return;

    setLoading(true);
    setError(null);

    try {
      // Get setup intent from backend
      const { clientSecret } = await paymentMethodsAPI.setupCard();

      // Confirm setup with card
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) throw new Error('Card element not found');

      const { error: stripeError } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElement,
        },
      });

      if (stripeError) {
        setError(stripeError.message || 'Failed to save card');
      } else {
        onSuccess();
        onClose();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to save card');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Credit Card</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
            Save a credit or debit card for future deposits and auto-refill.
          </Typography>

          <Box
            sx={{
              border: '1px solid #ccc',
              borderRadius: 1,
              p: 2,
              backgroundColor: loading ? '#f5f5f5' : '#fff',
            }}
          >
            <CardElement options={CARD_ELEMENT_OPTIONS} />
          </Box>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Test Mode:</strong> Use 4242 4242 4242 4242
            </Typography>
          </Alert>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!stripe || loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Saving...' : 'Save Card'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function AddCardDialogWrapper(props: { open: boolean; onClose: () => void; onSuccess: () => void }) {
  return (
    <Elements stripe={stripePromise}>
      <AddCardDialog {...props} />
    </Elements>
  );
}

export default function PaymentMethodsManager() {
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addCardOpen, setAddCardOpen] = useState(false);

  useEffect(() => {
    loadPaymentMethods();
  }, []);

  const loadPaymentMethods = async () => {
    try {
      setLoading(true);
      const data = await paymentMethodsAPI.list();
      setPaymentMethods(data.paymentMethods || []);
      setError(null);
    } catch (err: any) {
      setError('Failed to load payment methods');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (paymentMethodId: string) => {
    if (!confirm('Are you sure you want to remove this payment method?')) {
      return;
    }

    try {
      await paymentMethodsAPI.remove(paymentMethodId);
      loadPaymentMethods();
    } catch (err: any) {
      alert('Failed to remove payment method');
    }
  };

  const renderPaymentMethod = (pm: PaymentMethod) => {
    const isCard = pm.type === 'card';

    return (
      <ListItem key={pm.paymentMethodId}>
        <Box sx={{ mr: 2 }}>
          {isCard ? <CardIcon color="primary" /> : <BankIcon color="primary" />}
        </Box>
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body1">
                {isCard
                  ? `${pm.card!.brand} •••• ${pm.card!.last4}`
                  : `${pm.bankAccount!.bankName} •••• ${pm.bankAccount!.last4}`}
              </Typography>
            </Box>
          }
          secondary={
            isCard
              ? `Expires ${pm.card!.expMonth}/${pm.card!.expYear}`
              : `${pm.bankAccount!.accountType} account`
          }
        />
        <ListItemSecondaryAction>
          <IconButton edge="end" onClick={() => handleRemove(pm.paymentMethodId)}>
            <DeleteIcon />
          </IconButton>
        </ListItemSecondaryAction>
      </ListItem>
    );
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Payment Methods</Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setAddCardOpen(true)}
          >
            Add Card
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
          Saved payment methods for deposits and auto-refill
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : paymentMethods.length === 0 ? (
          <Alert severity="info">
            <Typography variant="body2">
              No payment methods saved. Add a card to enable auto-refill and faster deposits.
            </Typography>
          </Alert>
        ) : (
          <List>
            {paymentMethods.map(renderPaymentMethod)}
          </List>
        )}

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>💰 Save Money!</strong> ACH bank account: 0.8% fee vs Credit card: 2.9% + $0.30
          </Typography>
          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
            Example: $500 deposit costs $4 (ACH) vs $14.80 (card) = Save $10.80!
          </Typography>
        </Alert>
      </CardContent>

      <AddCardDialogWrapper
        open={addCardOpen}
        onClose={() => setAddCardOpen(false)}
        onSuccess={() => {
          setAddCardOpen(false);
          loadPaymentMethods();
        }}
      />
    </Card>
  );
}
